import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { useSession } from "@/context/SessionContext";
import { restaurantConfig, serviceStaff } from "@/config/restaurantConfig";
import { scopedKey, readJSON, writeJSON, makeId } from "@/lib/storage";
import { ORDER_STATUS, advanceOrder, isOrderOpen, migrateOrder, toOrderLines } from "@/lib/orders";
import { isApiMenuSource, guestMenuApiBase } from "@/config/menuSource";
import { fetchGuestOrderStatus, toLocalOrder, toPwaStatus } from "@/lib/guestOrder";
import { activeGuestEntry, safeSessionStorage } from "@/components/GuestMenuGate";
import { WAITER_CHOICES, createServiceRequest, fetchServiceRequestStatus } from "@/lib/guestService";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const OrderContext = createContext(null);
const ordersKey = () => scopedKey("orders");
const actionsKey = () => scopedKey("actions");
const MAX_ORDERS = 20;

// Mock status progression delay (ms) — keeps the demo lively but not too slow.
// Real status changes come from the restaurant (KDS/backend), never the client.
export const STATUS_ADVANCE_MS = 14000;
// Real (API) orders: status is polled from the backend, never advanced locally.
export const ORDER_POLL_MS = 15000;
// Real waiter/bill/help requests: status polled until claimed/resolved (max 30 min).
export const SERVICE_POLL_MS = 10000;

export function OrderProvider({ children }) {
  const { table, sessionId } = useSession();
  const { t } = useLang();
  // Newest first. orders[0] is the order the customer is currently looking at.
  const [orders, setOrders] = useState(() => readJSON(ordersKey(), []).map(migrateOrder));
  const [actions, setActions] = useState(() => readJSON(actionsKey(), []));

  useEffect(() => { writeJSON(ordersKey(), orders.length ? orders : null); }, [orders]);
  useEffect(() => { writeJSON(actionsKey(), actions); }, [actions]);

  const order = orders[0] || null;
  const activeOrder = isOrderOpen(order) ? order : null;

  // Auto-advance the open order to simulate kitchen progress up to COMPLETED.
  // Mock orders only: a real order's status belongs to the restaurant.
  useEffect(() => {
    if (!activeOrder || activeOrder.remoteToken) return undefined;
    const timer = setTimeout(() => {
      setOrders((prev) => (prev[0]?.id === activeOrder.id ? [advanceOrder(prev[0]), ...prev.slice(1)] : prev));
    }, STATUS_ADVANCE_MS);
    return () => clearTimeout(timer);
  }, [activeOrder]);

  const placeOrder = useCallback(({ detailed, subtotal }) => {
    const now = Date.now();
    const newOrder = {
      id: makeId("order"),
      number: String(Math.floor(1000 + Math.random() * 9000)),
      restaurantId: restaurantConfig.id,
      branchId: restaurantConfig.branch.id,
      tableNumber: table?.tableNumber ?? 12,
      sessionId,
      staffId: serviceStaff.id, // mock ServiceAssignment for this table
      items: toOrderLines(detailed),
      total: subtotal,
      status: ORDER_STATUS.PLACED,
      placedAt: now,
      estReadyMin: restaurantConfig.avgPrepTimeMin,
      statusHistory: [{ status: ORDER_STATUS.PLACED, at: now }],
    };
    setOrders((prev) => [newOrder, ...prev].slice(0, MAX_ORDERS));
    return newOrder;
  }, [table, sessionId]);

  // Append items to the open order without resetting its status.
  const addToOrder = useCallback(({ detailed, subtotal }) => {
    setOrders((prev) => {
      if (!isOrderOpen(prev[0])) return prev;
      const current = prev[0];
      return [{ ...current, items: [...current.items, ...toOrderLines(detailed)], total: current.total + subtotal }, ...prev.slice(1)];
    });
  }, []);

  const clearOrder = useCallback(() => setOrders([]), []);

  // Record an order accepted by the FudiTap backend (API mode).
  const recordRemoteOrder = useCallback((remote) => {
    const local = toLocalOrder(remote, { sessionId, restaurantId: restaurantConfig.id, branchId: restaurantConfig.branch.id });
    setOrders((prev) => [local, ...prev.filter((o) => o.id !== local.id)].slice(0, MAX_ORDERS));
    return local;
  }, [sessionId]);

  // Poll the real status of the open API order.
  const remoteToken = activeOrder?.remoteToken || null;
  useEffect(() => {
    if (!remoteToken || !isApiMenuSource()) return undefined;
    let cancelled = false;
    const poll = async () => {
      const result = await fetchGuestOrderStatus(activeGuestEntry(), remoteToken, { baseUrl: guestMenuApiBase() });
      if (cancelled || !result.ok) return;
      const status = toPwaStatus(result.order.status);
      setOrders((prev) => prev.map((o) => (o.remoteToken !== remoteToken || o.remoteStatus === result.order.status ? o : {
        ...o, remoteStatus: result.order.status, status,
        statusHistory: o.status === status ? o.statusHistory : [...(o.statusHistory || []), { status, at: Date.now() }],
      })));
    };
    const timer = setInterval(poll, ORDER_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [remoteToken]);

  const addAction = useCallback((action) => {
    const now = Date.now();
    setActions((prev) => [{ ...action, sessionId, id: makeId("act"), at: now }, ...prev].slice(0, 20));
  }, [sessionId]);

  // Waiter / bill / help. API mode: a real request (or an explicit failure reason);
  // mock mode (development/test only): the local demo action.
  const requestService = useCallback(async (kind, choiceKey = null) => {
    if (!isApiMenuSource()) {
      addAction({ type: kind, reasonKey: choiceKey });
      return { ok: true, mock: true };
    }
    const [type, reason] = kind === "bill" ? ["BILL", null] : WAITER_CHOICES[choiceKey] || ["WAITER", "other"];
    const result = await createServiceRequest(activeGuestEntry(), type, reason, { baseUrl: guestMenuApiBase(), storage: safeSessionStorage() });
    if (result.ok) {
      addAction({ type: kind, reasonKey: choiceKey, remoteToken: result.request.requestToken, remoteStatus: result.request.status });
    }
    return result;
  }, [addAction]);

  const liveServiceTokens = actions
    .filter((a) => a.remoteToken && (a.remoteStatus === "OPEN" || a.remoteStatus === "CLAIMED") && Date.now() - a.at < 30 * 60000)
    .map((a) => a.remoteToken).join(",");
  useEffect(() => {
    if (!liveServiceTokens || !isApiMenuSource()) return undefined;
    let cancelled = false;
    const poll = async () => {
      for (const token of liveServiceTokens.split(",")) {
        const result = await fetchServiceRequestStatus(activeGuestEntry(), token, { baseUrl: guestMenuApiBase() });
        if (cancelled || !result.ok) continue;
        setActions((prev) => prev.map((a) => {
          if (a.remoteToken !== token || a.remoteStatus === result.request.status) return a;
          if (result.request.status === "CLAIMED") toast(t("waiterOnWay"));
          return { ...a, remoteStatus: result.request.status };
        }));
      }
    };
    const timer = setInterval(poll, SERVICE_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [liveServiceTokens, t]);

  const value = useMemo(
    () => ({ order, activeOrder, orders, actions, placeOrder, clearOrder, addToOrder, addAction, recordRemoteOrder, requestService }),
    [order, activeOrder, orders, actions, placeOrder, clearOrder, addToOrder, addAction, recordRemoteOrder, requestService]
  );
  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
}

export function useOrder() {
  const ctx = useContext(OrderContext);
  if (!ctx) throw new Error("useOrder must be used within OrderProvider");
  return ctx;
}
